<script>
import { AuthService } from "@kinetics254/cassandra-base/modules/auth";
import helper from "@/packages/cassandra-base/utilities/helpers/helper";

export default {
  name: "GrievanceCaseForm",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    record: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        systemId: "",
        caseType: "",
        description: "",
        employeeNo: "",
        attachments: [],
      },
      valid: true,
      loading: false,
      attachmentError: false,
      attachmentErrorMsg: "",
      required: [(v) => !!v || "Field is required"],
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    caseTypes() {
      return this.$store.getters["Grievances/grievanceGetters"]("caseTypes");
    },
    user() {
      return AuthService.user;
    },
  },
  watch: {
    record: function (newVal) {
      if (newVal) {
        this.form.systemId = newVal.systemId;
        this.form.caseType = newVal.caseType;
        this.form.description = newVal.description;
        // Note: Attachments are typically not loaded here as they need separate API call
      }
    },
  },
  methods: {
    async submit() {
      if (this.$refs.form.validate()) {
        this.loading = true;

        try {
          // Prepare case data with all fields
          const caseData = {
            employeeNo: this.user.employee,
            caseType: this.form.caseType,
            description: this.form.description,
            attachments: this.form.attachments,
          };

          // Add systemId if editing
          if (this.form.systemId) {
            caseData.systemId = this.form.systemId;
          }

          // Determine operation (addCase or updateCase)
          const operation = this.form.systemId ? "updateCase" : "addCase";

          // Dispatch with complete form data
          const response = await this.$store.dispatch(
            `Grievances/${operation}`,
            caseData
          );

          if (response) {
            this.$refs.form.reset();
            this.resetForm();
            this.close();
            this.$emit(
              "success",
              `Grievance case ${
                this.form.systemId ? "updated" : "created"
              } successfully`
            );
          }
        } catch (error) {
          console.error("Error submitting form:", error);
          this.$emit(
            "error",
            error.response?.data?.message ||
              `Failed to ${
                this.form.systemId ? "update" : "create"
              } grievance case`
          );
        } finally {
          this.loading = false;
          this.resetForm();
          this.close();
        }
      }
    },

    async uploadAttachments(systemId) {
      const uploadPromises = this.form.attachments.map(async (attachment) => {
        try {
          // Prepare attachment data for backend
          const attachmentData = {
            sourceRecId: systemId,
            fileName: attachment.fileName,
            fileType: attachment.fileType,
            fileExtension: attachment.fileExtension,
            attachment: attachment.attachment, // Base64 content
          };

          // Dispatch to upload attachment
          await this.$store.dispatch(
            "Grievances/uploadAttachment",
            attachmentData
          );
        } catch (error) {
          console.error(
            `Failed to upload attachment ${attachment.fileName}:`,
            error
          );
          throw error;
        }
      });

      await Promise.all(uploadPromises);
    },

    close() {
      this.dialog = false;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        systemId: "",
        caseType: "",
        description: "",
        employeeNo: "",
        attachments: [],
      };
      this.attachmentError = false;
      this.attachmentErrorMsg = "";

      // Reset file input
      if (this.$refs.uploader) {
        this.$refs.uploader.reset();
      }
    },

    async uploadFile(value) {
      // Reset error state
      this.attachmentError = false;
      this.attachmentErrorMsg = "";

      if (!value || value.length === 0) {
        return;
      }

      // Validate file count
      if (value.length > 5) {
        this.attachmentError = true;
        this.attachmentErrorMsg = "Maximum 5 files allowed";
        return;
      }

      // Process each file
      for (const item of value) {
        try {
          // Validate file size (2MB limit)
          if (item.size > 2000000) {
            this.attachmentError = true;
            this.attachmentErrorMsg = `File ${item.name} exceeds 2MB limit`;
            continue;
          }

          // Validate file type
          const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/png",
            "image/gif",
            "image/jpg",
          ];
          if (!allowedTypes.includes(item.type)) {
            this.attachmentError = true;
            this.attachmentErrorMsg = `File ${item.name} has invalid type. Allowed: PDF, JPEG, PNG, GIF`;
            continue;
          }

          // Get file extension from name
          const fileExtension = item.name.split(".").pop() || "";

          // Check if file already exists in attachments
          if (this.form.attachments.some((el) => el.fileName === item.name)) {
            continue; // Skip if file already exists
          }

          // Convert to base64
          const base64Result = await helper.toBase64(item);
          const base64Content = base64Result.split(",")[1];

          // Add to attachments
          this.form.attachments.push({
            attachment: base64Content,
            fileName: item.name,
            fileType: item.type || `application/${fileExtension}`,
            fileExtension: fileExtension,
          });
        } catch (error) {
          console.error("Error processing file:", error);
          this.attachmentError = true;
          this.attachmentErrorMsg = `Error processing file ${item.name}`;
        } finally {
          this.this.close();
        }
      }
    },

    removeAttachment(index) {
      this.form.attachments.splice(index, 1);
    },

    async removeBase64(item) {
      const data = { systemId: item };

      console.log("delete items", data);
      await this.$store.dispatch("Grievances/deleteGrievanceAttachment", data);
      this.close();
    },
  },
};
</script>

<template>
  <v-dialog v-model="dialog" persistent max-width="600px">
    <v-card>
      <v-card-title>
        <span class="text-h5"
          >{{ record ? "Edit" : "New" }} Grievance Case</span
        >
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <!-- Case Type Select -->
          <v-select
            outlined
            :items="caseTypes"
            label="Case Type *"
            v-model="form.caseType"
            :rules="required"
            item-text="name"
            item-value="id"
          ></v-select>

          <!-- Description Textarea -->
          <v-textarea
            outlined
            label="Description *"
            v-model="form.description"
            rows="3"
            :rules="required"
          ></v-textarea>

          <div v-if="record" class="">
            <v-chip
              v-for="attachment in record.discDocumentAttachments"
              :key="attachment.id"
              close
              dark
              class="mr-2 mb-2"
              color="green lighten-1"
              label
            >
              <span class="table-font"
                >{{ attachment.fileName }}.{{ attachment.fileExtension }}</span
              >
            </v-chip>
          </div>

          <!-- File Upload Section -->
          <v-card outlined class="pa-3 mb-3">
            <v-card-text class="pa-0">
              <div class="d-flex align-center mb-2">
                <v-icon left>mdi-paperclip</v-icon>
                <span class="subtitle-1"
                  >Attachments (Max 5 files, 2MB each)</span
                >
              </div>

              <!-- File Input -->
              <v-file-input
                accept=".pdf,.jpeg,.jpg,.png,.gif"
                ref="uploader"
                label="Choose files"
                prepend-icon="mdi-camera"
                multiple
                chips
                :error="attachmentError"
                :error-messages="attachmentErrorMsg"
                @change="uploadFile"
                :disabled="loading"
                hint="Allowed: PDF, JPEG, PNG, GIF"
                persistent-hint
              ></v-file-input>

              <!-- Selected Files List -->
              <v-list v-if="form.attachments.length > 0" dense class="mt-2">
                <v-list-item
                  v-for="(file, index) in form.attachments"
                  :key="index"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-file-document</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ file.fileName }}</v-list-item-title>
                    <v-list-item-subtitle>
                      Type: {{ file.fileType }} | Extension:
                      {{ file.fileExtension }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="removeAttachment(index)"
                      :disabled="loading"
                    >
                      <v-icon color="error">mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>

          <!-- Action Buttons -->
          <v-card-actions class="px-0">
            <v-spacer></v-spacer>
            <v-btn @click="close" :disabled="loading" class="mr-2"
              >Cancel</v-btn
            >
            <v-btn
              color="primary"
              @click="submit"
              :disabled="loading"
              :loading="loading"
            >
              {{ record ? "Update" : "Save" }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
